/**
 * AchievementBadge - Componente de badge de conquista
 * ProfessionalAchievements - Lista de conquistas do profissional
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Dependências:
 * - Badge (shadcn/ui)
 * - axios
 * - API backend
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Badge } from "./ui/badge";
import { useTranslation } from "../translations";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AchievementBadge - Exibe um badge de conquista individual
 * 
 * @param {string} achievement - ID da conquista
 * @param {string} size - Tamanho: 'sm' ou 'lg'
 * @param {string} className - Classes CSS adicionais
 */
export const AchievementBadge = ({ achievement, size = "sm", className = "" }) => {
  const [definitions, setDefinitions] = useState({});

  const fetchAchievementDefinitions = async () => {
    try {
      const response = await axios.get(`${API}/api/achievements/definitions`);
      setDefinitions(response.data.achievements);
    } catch (error) {
      console.error("Error fetching achievement definitions:", error);
    }
  };

  useEffect(() => {
    fetchAchievementDefinitions();
  }, []);

  if (!achievement || !definitions[achievement]) {
    return null;
  }

  const def = definitions[achievement];
  const tier = definitions[achievement]?.tier || 'basic';
  const tierColors = {
    basic: 'bg-gray-100 text-gray-700 border-gray-200',
    bronze: 'bg-orange-100 text-orange-700 border-orange-200',
    silver: 'bg-slate-100 text-slate-700 border-slate-200',
    gold: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    platinum: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    diamond: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    specialist: 'bg-indigo-100 text-indigo-700 border-indigo-200'
  };

  const sizeClasses = size === 'lg' ? 'px-4 py-2 text-base' : 'px-2 py-1 text-xs';

  return (
    <Badge 
      className={`${tierColors[tier]} ${sizeClasses} ${className}`}
      title={def.description}
    >
      <span className="mr-1">{def.icon}</span>
      {def.name}
    </Badge>
  );
};

/**
 * ProfessionalAchievements - Lista de conquistas com opção de expandir
 * 
 * @param {array} achievements - Lista de IDs de conquistas
 * @param {string} size - Tamanho dos badges
 * @param {number} maxShow - Máximo de conquistas a mostrar antes de "ver mais"
 */
export const ProfessionalAchievements = ({ achievements = [], size = "sm", maxShow = 3 }) => {
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslation();
  
  if (!achievements || achievements.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        {t('noAchievementsYet')}
      </div>
    );
  }

  const displayAchievements = showAll ? achievements : achievements.slice(0, maxShow);
  const hasMore = achievements.length > maxShow;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {displayAchievements.map((achievement, index) => (
          <AchievementBadge 
            key={index} 
            achievement={achievement} 
            size={size}
          />
        ))}
        {hasMore && !showAll && (
          <button
            onClick={() => setShowAll(true)}
            className="px-2 py-1 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded-full hover:bg-blue-100"
          >
            +{achievements.length - maxShow} {t('showMore')}
          </button>
        )}
      </div>
      {showAll && hasMore && (
        <button
          onClick={() => setShowAll(false)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          {t('showLess')}
        </button>
      )}
    </div>
  );
};

export default AchievementBadge;
